/**
 * Brouillon de routine — conversions entre le formulaire et l'API.
 *
 * Même découpage que `templateDraft.ts`, `foodDraft.ts` et `exerciseDraft.ts` :
 * l'éditeur ne manipule que des chaînes (c'est ce qu'un `<input>` rend), et
 * tout ce qui ressemble à une conversion vit ici, testable sans monter un
 * composant. Un module qui exporte des composants ne doit rien exporter
 * d'autre (`react-refresh`), d'où le fichier séparé.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 LA CLÉ DE RENDU **EST** L'ADRESSE DE LA RESSOURCE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * `MealDraft.key` était « une clé de rendu locale — jamais envoyée au serveur »
 * (étape 5 de la V1). Ici c'est l'inverse : `clientId` est tiré à la **création
 * du bloc ou de la ligne dans l'éditeur**, il sert de `key` React *et* il est
 * envoyé. C'est le §2.3-b tout entier — le client possède l'identité de ce
 * qu'il manipule — et ce que ça achète est concret :
 *
 *   - composer une routine hors ligne ne demande **aucun place-titre**
 *     `{{op:n}}`, même quand elle pointe vers un exercice créé dans la minute ;
 *   - un double tap sur « Enregistrer » réécrit le **même** arbre au lieu d'en
 *     créer un second ;
 *   - réenregistrer une routine ne renumérote pas ses blocs : le piège de
 *     l'étape 7 (« les `id` d'ingrédient changent à chaque `PATCH` ») ne se
 *     représente pas, parce que l'adresse n'est plus l'`id`.
 */

import type {
  ProgressionKind,
  RoutineBlockKind,
  RoutineDTO,
  RoutineInput,
  SetKind,
} from '../../../../shared/types';
import { newClientId } from '../../lib/dates';
import { toNumber } from '../../lib/formFields';

/* ==========================================================================
 * Le brouillon
 * ======================================================================= */

/**
 * Comment se mesure une série : en répétitions, ou en secondes.
 *
 * 🔴 **Un état de FORMULAIRE, pas une colonne.** En base, c'est
 * `duration_seconds IS NOT NULL` qui décide (cf. `shared/types.ts`) — la forme
 * se lit dans les données. Mais un formulaire, lui, doit savoir quel champ
 * afficher **avant** qu'il soit rempli : basculer sur « Durée » puis vider le
 * champ ne doit pas faire retomber la ligne en répétitions sous les doigts.
 * D'où ce drapeau ici, et lui seulement ici.
 */
export type LineMeasure = 'reps' | 'time';

export interface LineDraft {
  /** 🔴 `clientId` **et** clé de rendu — cf. l'en-tête. */
  clientId: string;
  exerciseClientId: string;
  /** Affichage seulement : le serveur rejoint le nom à la lecture, il ne le copie pas. */
  exerciseName: string;
  /**
   * La vignette — **affichage seulement, et jamais transmise** *(31/08/2026,
   * demandé après usage : « ajoute les vignettes des exos quand on édite une
   * routine »)*.
   *
   * 🔴 **Elle voyage à CÔTÉ de ce qui part au serveur**, exactement comme
   * `PlannedSet.media` à l'étape 6, et pour la même raison : les routes
   * refusent `mediaKey` par construction (§4.1 — ce serait laisser le client
   * désigner un chemin sur le disque du serveur). `inputOfRoutineDraft`
   * compose un objet explicite champ par champ, donc ces deux-là ne peuvent
   * pas fuir.
   *
   * ⚠️ **Rien de nouveau n'a été demandé au réseau** : `RoutineLineDTO` les
   * porte depuis l'étape 5 et le sélecteur les rend déjà. On les jetait.
   */
  exerciseMediaKey: string | null;
  exerciseImageCount: number;
  sets: string;
  measure: LineMeasure;
  repMin: string;
  repMax: string;
  /** En secondes. Lu seulement quand `measure` vaut `time`. */
  durationSeconds: string;
  targetWeightKg: string;
  targetRpe: string;
  setKind: SetKind;
  note: string;
  progressionKind: ProgressionKind;
  progressionIncrementKg: string;
}

export interface BlockDraft {
  clientId: string;
  kind: RoutineBlockKind;
  rounds: string;
  restSeconds: string;
  lines: LineDraft[];
}

export interface RoutineDraft {
  name: string;
  notes: string;
  /**
   * L'échauffement — une étape par ligne.
   *
   * ⚠️ **Distinct de `notes`**, et pas par symétrie : un échauffement se lit
   * *avant* de commencer et se suit dans l'ordre, une note se lit quand on en a
   * besoin. Cf. `routine.warmup` au schéma.
   */
  warmup: string;
  blocks: BlockDraft[];
}

/* ==========================================================================
 * Conversions
 * ======================================================================= */

/**
 * Chaîne → nombre **ou absence**.
 *
 * 🔴 **`toNumber()` de `lib/formFields` ne convient pas ici, et la nuance
 * compte** : il fait valoir zéro à un champ vide, ce qui est exactement le bon
 * comportement pour une macro (ne rien manger, c'est zéro gramme) et exactement
 * le mauvais pour une charge cible. **0 kg est une valeur** — c'est un exercice
 * au poids du corps —, tandis qu'un champ vide veut dire « je ne me fixe pas de
 * charge ». Les confondre écrirait « 0 kg visé » sur toutes les lignes qu'on
 * n'a pas renseignées, et la progression de l'étape 7 les lirait comme telles.
 */
export function toOptional(raw: string): number | null {
  return raw.trim() === '' ? null : toNumber(raw);
}

/** Nombre → champ, en gardant le zéro visible — cf. `toOptional`. */
function fromOptional(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

/**
 * Bloc d'un seul exercice — l'**emballage** d'un exercice ajouté directement à
 * la routine.
 *
 * 🔴 **`single` n'est plus une nature de bloc qu'on choisit, c'est une nature
 * qu'on obtient** (retours du 28/08/2026). L'interface propose « Ajouter un
 * exercice » et « Ajouter un bloc » : le premier crée ceci, et l'utilisateur ne
 * voit jamais le mot « bloc ». Le second crée un groupe, superset ou circuit.
 *
 * ⚠️ **Le modèle, lui, ne bouge pas** : une routine reste une liste ordonnée de
 * blocs, et un exercice isolé est un bloc à une ligne. L'alternative — des
 * lignes rattachées directement à la routine, à côté des blocs — demanderait
 * d'ordonner deux tables dans un même espace de positions, pour un gain
 * strictement nul à l'écran. C'est l'emballage qui devient invisible, pas le
 * modèle qui change.
 */
export function singleBlock(): BlockDraft {
  return { clientId: newClientId(), kind: 'single', rounds: '3', restSeconds: '90', lines: [] };
}

/**
 * Bloc de groupe — superset par défaut.
 *
 * ⚠️ **Superset et non circuit** : c'est le groupe le plus courant, et le seul
 * des deux qui ne demande pas un second réglage (`rounds`) pour être valide.
 */
export function groupBlock(): BlockDraft {
  return { clientId: newClientId(), kind: 'superset', rounds: '3', restSeconds: '90', lines: [] };
}

/**
 * Ligne vierge pour un exercice donné.
 *
 * ⚠️ **`3 × 8-12` en défaut**, et non des champs vides : c'est la plage la plus
 * commune en hypertrophie, et un formulaire qui démarre rempli se corrige plus
 * vite qu'il ne se remplit — même raisonnement qu'aux défauts `strength` /
 * `intermediate` de l'éditeur d'exercice. Rien n'est contraint, tout se change.
 */
export function lineFor(
  exerciseClientId: string,
  exerciseName: string,
  /** Vignette, telle que le sélecteur la rend déjà — cf. `LineDraft`. */
  media: { mediaKey: string | null; imageCount: number } = { mediaKey: null, imageCount: 0 },
): LineDraft {
  return {
    clientId: newClientId(),
    exerciseClientId,
    exerciseName,
    exerciseMediaKey: media.mediaKey,
    exerciseImageCount: media.imageCount,
    sets: '3',
    /*
     * ⚠️ **En répétitions par défaut**, et pas de détection automatique. On
     * pourrait deviner « en durée » d'après le nom (« plank », « hold »…) : ce
     * serait juste une fois sur deux, faux en silence l'autre fois, et
     * impossible à comprendre depuis l'écran. Une bascule visible coûte un tap.
     */
    measure: 'reps',
    repMin: '8',
    repMax: '12',
    durationSeconds: '45',
    targetWeightKg: '',
    targetRpe: '',
    setKind: 'work',
    note: '',
    progressionKind: 'none',
    progressionIncrementKg: '2,5',
  };
}

export function emptyRoutineDraft(): RoutineDraft {
  return { name: '', notes: '', warmup: '', blocks: [] };
}

export function draftOfRoutine(routine: RoutineDTO): RoutineDraft {
  return {
    name: routine.name,
    notes: routine.notes ?? '',
    warmup: routine.warmup ?? '',
    blocks: routine.blocks.map((block) => ({
      clientId: block.clientId,
      kind: block.kind,
      /*
       * ⚠️ Le repli sur « 3 » et « 90 » n'écrit rien : ce sont les valeurs que
       * le champ **propose** quand la routine relue n'en portait pas. Le
       * serveur, lui, a mis `rounds` à `null` pour un bloc non-circuit — le
       * relire vide et le réafficher vide obligerait à ressaisir un nombre de
       * tours en passant un bloc en circuit.
       */
      rounds: block.rounds === null ? '3' : String(block.rounds),
      restSeconds: block.restSeconds === null ? '' : String(block.restSeconds),
      lines: block.lines.map((line) => ({
        clientId: line.clientId,
        exerciseClientId: line.exerciseClientId,
        exerciseName: line.exerciseName,
        /* `RoutineLineDTO` les porte depuis l'étape 5 — cf. `LineDraft`. */
        exerciseMediaKey: line.exerciseMediaKey,
        exerciseImageCount: line.exerciseImageCount,
        sets: String(line.sets),
        /*
         * ⚠️ La nature de la ligne se **relit** dans les données (§ `durationSeconds`
         * de `shared/types.ts`), elle n'est pas transportée : c'est le seul
         * endroit où le drapeau de formulaire se reconstitue.
         *
         * Et comme pour `rounds`, les champs de la mesure non retenue prennent
         * une valeur proposée plutôt que le vide : basculer une ligne en durée
         * ne doit pas obliger à ressaisir un nombre depuis rien.
         */
        measure: line.durationSeconds === null ? 'reps' : 'time',
        repMin: line.repMin === null ? '8' : String(line.repMin),
        repMax: line.repMax === null ? '12' : String(line.repMax),
        durationSeconds: line.durationSeconds === null ? '45' : String(line.durationSeconds),
        targetWeightKg: fromOptional(line.targetWeightKg),
        targetRpe: fromOptional(line.targetRpe),
        setKind: line.setKind,
        note: line.note ?? '',
        progressionKind: line.progressionKind,
        progressionIncrementKg:
          line.progressionIncrementKg === null
            ? '2,5'
            : String(line.progressionIncrementKg).replace('.', ','),
      })),
    })),
  };
}

/**
 * Brouillon → corps de `PUT`.
 *
 * ⚠️ **Aucune `position` n'est transmise** : le rang est celui du tableau, et
 * c'est le serveur qui renumérote de 0 à n−1. C'est ce qui rend le
 * réordonnancement trivial — on déplace un élément, on réenregistre.
 *
 * ⚠️ **`rounds` et `progressionIncrementKg` sont envoyés même quand ils ne
 * s'appliquent pas** ; le serveur les force à `null` selon le `kind` et le
 * `progressionKind` (§4.3). Les filtrer ici aussi ferait exister la règle à
 * deux endroits, et c'est le serveur qui doit la tenir — il est le seul que la
 * file ne contourne pas.
 */
export function inputOfRoutineDraft(draft: RoutineDraft): RoutineInput {
  return {
    name: draft.name.trim(),
    notes: draft.notes.trim() === '' ? null : draft.notes.trim(),
    warmup: draft.warmup.trim() === '' ? null : draft.warmup.trim(),
    blocks: draft.blocks.map((block) => ({
      clientId: block.clientId,
      kind: block.kind,
      rounds: toOptional(block.rounds),
      restSeconds: toOptional(block.restSeconds),
      lines: block.lines.map((line) => ({
        clientId: line.clientId,
        exerciseClientId: line.exerciseClientId,
        sets: Math.round(toNumber(line.sets)),
        /*
         * 🔴 **Exactement l'une des deux mesures est envoyée**, jamais les deux :
         * le serveur refuse une ligne qui en porterait deux (`measure_required`),
         * et il a raison — elle dirait deux choses. C'est ici que le drapeau de
         * formulaire redevient une donnée.
         */
        repMin: line.measure === 'reps' ? Math.round(toNumber(line.repMin)) : null,
        repMax: line.measure === 'reps' ? Math.round(toNumber(line.repMax)) : null,
        durationSeconds:
          line.measure === 'time' ? Math.round(toNumber(line.durationSeconds)) : null,
        targetWeightKg: toOptional(line.targetWeightKg),
        targetRpe: toOptional(line.targetRpe),
        setKind: line.setKind,
        note: line.note.trim() === '' ? null : line.note.trim(),
        progressionKind: line.progressionKind,
        progressionIncrementKg: toOptional(line.progressionIncrementKg),
      })),
    })),
  };
}

/* ==========================================================================
 * Ce qui empêche d'enregistrer, et pourquoi
 * ======================================================================= */

/**
 * Le premier motif de refus, en français — ou `null` si tout va bien.
 *
 * 🔴 **Une phrase, pas un booléen.** Les autres éditeurs du projet exposent un
 * `canSave*()` et se contentent de griser le bouton ; ici le formulaire porte
 * jusqu'à trente lignes réparties en blocs, et un bouton muet obligerait à
 * chercher soi-même laquelle cloche. La phrase renvoyée désigne le bloc et la
 * ligne, exactement comme les refus du serveur les désignent.
 *
 * ⚠️ **Elle double les contrôles du serveur, et c'est assumé** — même statut
 * que `foodPreview.ts` et `recipePreview.ts` (« doublons assumés ; en cas de
 * divergence, le serveur gagne »). Sans elle, le seul chemin vers l'erreur
 * serait un aller-retour, donc rien du tout hors ligne : la file mettrait
 * l'opération en attente, et le refus n'arriverait qu'au drainage, des heures
 * plus tard, très loin du geste qui l'a causé.
 */
export function routineDraftIssue(draft: RoutineDraft): string | null {
  if (draft.name.trim() === '') return 'Donnez un nom à la routine.';

  for (const [index, block] of draft.blocks.entries()) {
    const rang = index + 1;

    /*
     * ⚠️ Le message ne parle de « bloc » que pour un groupe. Un `single` est un
     * exercice ajouté directement : lui reprocher d'être un « bloc vide »
     * nommerait une notion que l'interface ne montre plus.
     */
    if (block.lines.length === 0) {
      return block.kind === 'single'
        ? `Un exercice de la routine n’a pas été choisi (rang ${String(rang)}).`
        : `Le bloc n° ${String(rang)} n’a aucun exercice.`;
    }

    if (block.kind === 'single' && block.lines.length > 1) {
      return `Un exercice isolé (rang ${String(rang)}) en contient ${String(
        block.lines.length,
      )} : passez-le en superset ou en circuit.`;
    }

    if (block.kind === 'circuit' && (toOptional(block.rounds) ?? 0) < 1) {
      return `Le circuit n° ${String(rang)} doit indiquer son nombre de tours.`;
    }

    for (const line of block.lines) {
      /*
       * ⚠️ **La ligne est désignée par son EXERCICE, plus par son rang.** « bloc
       * n° 2, ligne n° 3 » supposait que l'utilisateur compte les blocs — or
       * depuis les retours du 28/08 il n'en voit plus la plupart. Le nom de
       * l'exercice est ce qu'il a sous les yeux.
       */
      const ligne =
        block.kind === 'single'
          ? `« ${line.exerciseName} »`
          : `« ${line.exerciseName} », bloc n° ${String(rang)}`;
      if (Math.round(toNumber(line.sets)) < 1) return `Séries manquantes — ${ligne}.`;

      if (line.measure === 'time') {
        if (Math.round(toNumber(line.durationSeconds)) < 1) {
          return `Durée manquante — ${ligne}.`;
        }
      } else {
        const min = Math.round(toNumber(line.repMin));
        const max = Math.round(toNumber(line.repMax));
        if (min < 1 || max < 1) return `Répétitions manquantes — ${ligne}.`;
        if (min > max) return `La plage de répétitions est inversée — ${ligne}.`;
      }

      if (line.progressionKind === 'double' && (toOptional(line.progressionIncrementKg) ?? 0) <= 0) {
        return `La progression double demande un incrément — ${ligne}.`;
      }
    }
  }
  return null;
}

/* ==========================================================================
 * Formatage — lu par l'éditeur ET par la vue de détail
 * ======================================================================= */

/** « 8-12 » ou « 10 » — reps fixes = `repMin === repMax` (§4.1). */
export function formatReps(repMin: number, repMax: number): string {
  return repMin === repMax ? String(repMin) : `${String(repMin)}-${String(repMax)}`;
}

/**
 * « 1 min 30 » plutôt que « 90 s » — c'est ainsi qu'on lit une durée.
 *
 * ⚠️ **Une seule fonction pour le repos d'un bloc ET la durée d'une série.**
 * Ce sont les mêmes secondes lues par le même œil ; deux formatages auraient
 * affiché « 1 min 30 » d'un côté et « 90 s » de l'autre sur le même écran.
 */
export function formatSeconds(seconds: number | null): string | null {
  if (seconds === null) return null;
  if (seconds < 60) return `${String(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  const reste = seconds % 60;
  return reste === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(reste)}`;
}

/**
 * Ce qu'une ligne demande de faire : « 4 × 8-12 » ou « 3 × 45 s ».
 *
 * 🔴 **La nature de la ligne se lit dans les données**, pas dans un drapeau :
 * `durationSeconds !== null` **est** « en durée ». C'est la même discipline que
 * « reps fixes = `repMin === repMax` », et elle vaut aussi ici — le lecteur n'a
 * pas à chercher ailleurs ce que la ligne dit d'elle-même.
 */
export function formatMeasure(line: {
  sets: number;
  repMin: number | null;
  repMax: number | null;
  durationSeconds: number | null;
}): string {
  if (line.durationSeconds !== null) {
    return `${String(line.sets)} × ${formatSeconds(line.durationSeconds) ?? ''}`;
  }
  if (line.repMin === null || line.repMax === null) return `${String(line.sets)} séries`;
  return `${String(line.sets)} × ${formatReps(line.repMin, line.repMax)}`;
}

/**
 * Libellé français d'un type de bloc.
 *
 * ⚠️ **`single` y figure encore, et il n'est plus jamais affiché.** Le modèle
 * connaît les trois natures ; l'interface n'en propose que deux (cf.
 * `GROUP_KINDS`) et n'annonce jamais la troisième — un exercice ajouté
 * directement n'a pas à se présenter comme un bloc. Le libellé reste pour les
 * messages de diagnostic et pour qui relit le modèle.
 */
export const BLOCK_KIND_LABEL: Readonly<Record<RoutineBlockKind, string>> = {
  single: 'Exercice seul',
  superset: 'Superset',
  circuit: 'Circuit',
};

/**
 * Les natures qu'un **groupe** peut prendre — les seules que l'éditeur propose.
 *
 * 🔴 **`single` n'en est plus une** (retours du 28/08/2026). Il était offert
 * comme troisième choix, ce qui obligeait à créer un bloc puis à choisir « le
 * bloc qui n'en est pas un » pour ajouter un simple exercice. Le bouton
 * « Ajouter un exercice » fait ça d'un tap, et la nature `single` devient un
 * détail d'emballage que personne n'a à nommer.
 */
export const GROUP_KINDS: readonly Extract<RoutineBlockKind, 'superset' | 'circuit'>[] = [
  'superset',
  'circuit',
];

/**
 * Libellé français d'un type de série (specs §12.2).
 *
 * ⚠️ « Série longue » et non « AMRAP » : le mot anglais n'apparaît nulle part
 * ailleurs dans l'application, et les specs disent « série longue jusqu'à
 * l'échec ».
 */
export const SET_KIND_LABEL: Readonly<Record<SetKind, string>> = {
  warmup: 'Échauffement',
  work: 'Travail',
  drop: 'Drop set',
  amrap: 'Série longue',
};

export const PROGRESSION_LABEL: Readonly<Record<ProgressionKind, string>> = {
  none: 'Aucune',
  double: 'Double progression',
};
